import {
  ArrowRightIcon,
  Box,
  Button,
  ExternalLinkIcon,
  InfoIcon,
  Link,
  RefreshIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TablePagination,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  Tooltip,
  TooltipInteractiveWrapper,
} from '@razorpay/blade/components';
import { StatusBadge } from 'components/SalaryComponents/components/StatusBadge';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { ListViewLoader } from 'components/ui/ListViewLoader';

import { routePaths } from 'components/Routes/data';
import { TextWithDescription } from 'components/SalaryComponents/components/TextWithDescription';
import { StatusFilterKeyType } from 'components/SalaryComponents/components/filters/StatusFilter';
import {
  helpArticles,
  nonPayableBenefitsFiltersMap,
  salaryComponentParams,
  salaryComponentsQueries,
} from 'components/SalaryComponents/constants';
import { useDetailsView } from 'components/SalaryComponents/hooks';
import { getQueryVariables, getTableItemCountLimits } from 'components/SalaryComponents/utils';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import { getErrorsAsString } from 'components/Kyc/utils';
import { AppError } from 'utils/AppError';

import { useQuery } from 'react-query';
import { NonPayableBenefitsComponentType } from 'components/SalaryComponents/types';
import api from 'api';

const NonPayableBenefitsListView = ({
  onCountChange,
  counts,
}: {
  onCountChange: (counts: Record<StatusFilterKeyType, number>) => void;
  counts?: Record<StatusFilterKeyType, number>;
}) => {
  const history = useHistory();
  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const { currentFilters, isAnyFilterApplied } = useFilters({
    filtersMap: nonPayableBenefitsFiltersMap,
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  const queryVariables = getQueryVariables('npb', currentFilters);

  const { data, isLoading, isFetching, refetch, error, isError } = useQuery({
    queryKey: [salaryComponentsQueries.getSalaryComponentsList, queryVariables],
    queryFn: () =>
      api.salaryComponents.queries.listSalaryComponents<NonPayableBenefitsComponentType>(
        queryVariables,
      ),
    select(data) {
      return data.payrollListSalaryComponents.data.salaryComponents;
    },
  });

  const nodes = useMemo(() => data ?? [], [data]);
  const { openDetailsView } = useDetailsView(salaryComponentParams.componentId);

  // Use useEffect to calculate and set counts only when needed
  useEffect(() => {
    if (onCountChange && nodes.length > 0 && !counts) {
      onCountChange({
        all: nodes.length,
        active: nodes.filter((node) => node.isActive).length,
        disabled: nodes.filter((node) => !node.isActive).length,
      });
    }
  }, [nodes, onCountChange, counts]);

  const isTableEmpty = nodes.length === 0;
  const errorString = getErrorsAsString(error as AppError);

  return (
    <>
      <Table
        data={{ nodes }}
        pagination={
          !isTableEmpty ? (
            <TablePagination
              currentPage={currentPage}
              showPageSizePicker={true}
              onPageChange={({ page }) => {
                setCurrentPage(page);
              }}
              onPageSizeChange={({ pageSize }) => {
                setItemsPerPage(pageSize);
              }}
              totalItemCount={nodes.length}
            />
          ) : undefined
        }
        toolbar={
          <TableToolbar title={getTableItemCountLimits(nodes.length, currentPage, itemsPerPage)}>
            <TableToolbarActions>
              <Button icon={RefreshIcon} variant={'tertiary'} onClick={refetch} />
            </TableToolbarActions>
          </TableToolbar>
        }
        showBorderedCells={false}>
        {(salaryComponents) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Text weight="semibold">Component Name</Text>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Box display={'flex'} flexDirection={'row'} gap={'spacing.3'}>
                    <Text weight="semibold">Display Name</Text>
                    <Tooltip
                      placement="top"
                      content={`The name that will appear in employee's payslip and Salary Register`}>
                      <TooltipInteractiveWrapper>
                        <InfoIcon marginTop={'spacing.1'} />
                      </TooltipInteractiveWrapper>
                    </Tooltip>
                  </Box>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Text weight="semibold" marginX={'auto'}>
                    Status
                  </Text>
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            {isFetching ? (
              <ListViewLoader
                columnCount={4}
                accessibilityLabel="loading non-payable benefits components"
              />
            ) : (
              <TableBody>
                {salaryComponents.map((salaryComponent) => {
                  return (
                    <TableRow
                      key={salaryComponent.id}
                      item={salaryComponent}
                      onClick={() => openDetailsView(salaryComponent.id)}
                      hoverActions={
                        <Button
                          variant="tertiary"
                          color="primary"
                          size="medium"
                          icon={ArrowRightIcon}
                          iconPosition="right"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailsView(salaryComponent.id);
                          }}
                          testID={`view-details-button-${salaryComponent.id}`}>
                          View Details
                        </Button>
                      }>
                      <TableCell>
                        <TextWithDescription componentName={salaryComponent.name} />
                      </TableCell>
                      <TableCell>
                        <Text>
                          {salaryComponent.settings.behaviour.displayBehaviour?.displayName ??
                            salaryComponent.name}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Box marginX={'auto'}>
                          <StatusBadge isActive={salaryComponent.isActive} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </>
        )}
      </Table>
      {isTableEmpty && !isLoading && !isFetching && !isError && (
        <EmptyListView
          title={
            isAnyFilterApplied
              ? 'No Search Results Found'
              : 'No Non-payable Benefits components created yet 🙈'
          }
          description={
            <Text color={'surface.text.gray.muted'} weight={'medium'} textAlign={'center'}>
              Click on Create Component button to create your first component
            </Text>
          }>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={() => {
                  history.push(routePaths.salaryComponents.create.nonPayableBenefits.root);
                }}
                isFullWidth>
                Create Non-payable Benefits Component
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
      {isError && (
        <EmptyListView
          title={`There was an error while fetching the data for ${
            currentFilters.status ?? 'all'
          } non-payable benefits components. `}
          description={errorString ? `Error: ${errorString}` : null}>
          <Box display={'flex'} flexDirection={'column'} gap={'spacing.4'} marginX={'auto'}>
            <Link
              size="medium"
              variant="anchor"
              href={helpArticles.learnMore} // link to be added
              icon={ExternalLinkIcon}
              iconPosition="right"
              target="_blank">
              Learn more about salary component
            </Link>
            <Box marginX={'auto'}>
              <Button
                size="medium"
                onClick={refetch}
                isFullWidth
                iconPosition="left"
                icon={RefreshIcon}>
                Retry
              </Button>
            </Box>
          </Box>
        </EmptyListView>
      )}
    </>
  );
};

export default NonPayableBenefitsListView;
